import { NetworkTaxonomyData } from "@app/network-taxonomies/networkTaxonomy";
import { CountyStatus } from "@app/projects/create/countyStatus";
import CreateProject from "@app/projects/create/createProject";
import CreateProjectOverrideMemberSet from "@app/projects/createProjectOverrideMemberSet";
import { ProjectExclusionType, TimeDistanceCalculatorType } from "@app/projects/enumerators";
import { ResourceTagModel } from "@app/tags/import/types";

/**
 * The model used to request new project creation
 */
export interface CreateZipProjectRequestModel {
  name: string;
  description: string;
  isPrivate: boolean;
  tags?: ResourceTagModel[];
  memberSetId?: number;
  memberSetAlias: string;
  memberSetId2?: number;
  memberSetAlias2: string;
  networkProviderSetId: number;
  networkProviderSetAlias?: string;
  prospectProviderSetId?: number;
  useMarketProvidersForImpact: boolean;
  useMarketProvidersForMarketAnalysis: boolean;
  useProspectProvidersForMarketAnalysis: boolean;
  useNetworkProvidersForMarketAnalysis: boolean;
  marketProviderSetId?: number;
  marketProviderSetAlias?: string;
  nextClosestProviderSetAlias?: string;
  adequacyTemplateCollectionId?: number;
  adequacyTemplateCorrelationId: string;
  serviceAreaRegionSetId: number;
  networkExclusions: ProjectExclusionType[];
  impactExclusions: ProjectExclusionType[];
  excludedSpecialtyGroupCodes: string[];
  telehealthSpecialtyGroupCodes: string[];
  accessCodeMapSetId?: number;
  networkSpecialtyCodeMapSetId?: number;
  prospectSpecialtyCodeMapSetId?: number;
  prospectProviderSetAlias?: string;
  marketSpecialtyCodeMapSetId?: number;
  membershipRiskMemberSetId?: number;
  membershipRiskMemberSetAlias: string;
  strictTimeAndDistance: boolean;
  specialtyGroupNetworkProviderSetCodeMapSetId?: number;
  includeGapAging: boolean;
  showAttestation: boolean;
  showNppesDates: boolean;
  showAdvancedComparison: boolean;
  showClosestProviderReport: boolean;
  showNextClosestNetworkProviders: boolean;
  ncpInStateProvidersOnly: boolean;
  autoArchiveUnarchiveSpecialtyExceptions: boolean;
  showAverageDistance: boolean;
  showMaximumDistance: boolean;
  showAverageTime: boolean;
  showMaximumTime: boolean;
  countyStatuses: CountyStatus[];
  createProjectOverrideMemberSets: CreateProjectOverrideMemberSet[];
  timeDistanceCalculatorType: TimeDistanceCalculatorType;
  showMipsScore: boolean;
  showLocationConfidenceScores: boolean;
  showVaClaimsVolumeColumn: boolean;
  showTricareClaimsVolumeColumn: boolean;
  showCMSCredit: boolean;
  showPciData: boolean;
  showPciDashboard: boolean;
  showPgrConfidenceTier: boolean;
  networkTaxonomy: NetworkTaxonomyData;
  excludeInVolumeActions: boolean;
  runNow: boolean;
  publishOnCompletion?: boolean;
  overrideJobQueueName?: string;
  enableRegulatoryExceptionReports: boolean;
}

export const convertCreateProjectToZipProjectRequestModel = (
  project: CreateProject,
  runNow: boolean,
): CreateZipProjectRequestModel => {
  const zipRequestModel: CreateZipProjectRequestModel = {
    accessCodeMapSetId: project.accessCodeMapSetId,
    adequacyTemplateCollectionId: project.adequacyTemplateCollectionId,
    adequacyTemplateCorrelationId: project.adequacyTemplateCorrelationId,
    autoArchiveUnarchiveSpecialtyExceptions: project.autoArchiveUnarchiveSpecialtyExceptions,
    countyStatuses: project.countyStatuses,
    createProjectOverrideMemberSets: project.createProjectOverrideMemberSets,
    description: project.description,
    enableRegulatoryExceptionReports: project.enableRegulatoryExceptionReports,
    excludedSpecialtyGroupCodes: project.excludedSpecialtyGroupCodes,
    excludeInVolumeActions: project.excludeInVolumeActions,
    impactExclusions: project.impactExclusions as ProjectExclusionType[],
    includeGapAging: project.includeGapAging,
    isPrivate: project.isPrivate,
    marketProviderSetAlias: project.marketProviderSetAlias,
    marketProviderSetId: project.marketProviderSetId,
    marketSpecialtyCodeMapSetId: project.marketSpecialtyCodeMapSetId,
    memberSetAlias: project.memberSetAlias,
    memberSetAlias2: project.memberSetAlias2,
    memberSetId: project.memberSetId,
    memberSetId2: project.memberSetId2,
    membershipRiskMemberSetAlias: project.membershipRiskMemberSetAlias,
    membershipRiskMemberSetId: project.membershipRiskMemberSetId,
    name: project.name,
    ncpInStateProvidersOnly: project.ncpInStateProvidersOnly,
    networkExclusions: project.networkExclusions as ProjectExclusionType[],
    networkProviderSetAlias: project.networkProviderSetAlias,
    networkProviderSetId: project.networkProviderSetId,
    networkSpecialtyCodeMapSetId: null,
    networkTaxonomy: project.networkTaxonomy,
    nextClosestProviderSetAlias: project.nextClosestProviderSetAlias,
    prospectProviderSetAlias: project.prospectProviderSetAlias,
    prospectProviderSetId: project.prospectProviderSetId,
    prospectSpecialtyCodeMapSetId: null,
    runNow: runNow,
    serviceAreaRegionSetId: project.serviceAreaRegionSetId,
    showAdvancedComparison: project.showAdvancedComparison,
    showAttestation: project.showAttestation,
    showAverageDistance: project.showAverageDistance,
    showAverageTime: project.showAverageTime,
    showClosestProviderReport: project.showClosestProviderReport,
    showCMSCredit: project.showCMSCredit,
    showLocationConfidenceScores: project.showLocationConfidenceScores,
    showMaximumDistance: project.showMaximumDistance,
    showMaximumTime: project.showMaximumTime,
    showMipsScore: project.showMipsScore,
    showNextClosestNetworkProviders: project.showNextClosestNetworkProviders,
    showNppesDates: project.showNppesDates,
    showPciDashboard: project.showPciDashboard,
    showPciData: project.showPciData,
    showPgrConfidenceTier: project.showPgrConfidenceTier,
    showTricareClaimsVolumeColumn: project.showTricareClaimsVolumeColumn,
    showVaClaimsVolumeColumn: project.showVaClaimsVolumeColumn,
    specialtyGroupNetworkProviderSetCodeMapSetId: null,
    strictTimeAndDistance: project.strictTimeAndDistance,
    tags: project.tags,
    telehealthSpecialtyGroupCodes: project.telehealthSpecialtyGroupCodes,
    timeDistanceCalculatorType: project.timeDistanceCalculatorType,
    useMarketProvidersForImpact: project.useMarketProvidersForImpact,
    useMarketProvidersForMarketAnalysis: project.useMarketProvidersForMarketAnalysis,
    useNetworkProvidersForMarketAnalysis: project.useNetworkProvidersForMarketAnalysis,
    useProspectProvidersForMarketAnalysis: project.useProspectProvidersForMarketAnalysis,
    // only used by qesi
    // PublishOnCompletion: null,
    // OverrideJobQueueName: null,
  };

  return zipRequestModel;
};
